import { DashboardRole, DashboardSectionId } from "../../types.js";
import { useState, useCallback, useMemo } from "react";
import ManualsPurchaseModal from "../modals/ManualsPurchaseModal";
import { useRolePermissions } from "../../hooks/useRolePermissions";
import {
  LayoutDashboard,
  Package,
  MessageSquare,
  Settings,
  LogOut,
  BookOpen,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  FolderOpen,
  Image,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

/* ── Design tokens (inline clamp values from spec) ── */
const SIDEBAR_EXPANDED_WIDTH = 280;
const SIDEBAR_COLLAPSED_WIDTH = 80;

const clamp = {
  headerPx: "clamp(12px, 1.11vw, 16px)",
  headerMinH: "clamp(92px, 9.26vh, 132px)",
  logoSize: "clamp(28px, 2.08vw, 30px)",
  badgePx: "clamp(6px, 0.56vw, 8px)",
  badgePy: "clamp(2px, 0.21vh, 3px)",
  badgeFont: "clamp(11px, 0.83vw, 12px)",
  navPadX: "clamp(8px, 0.83vw, 12px)",
  navPadTop: "32px",
  navGap: "clamp(4px, 0.56vh, 8px)",
  itemH: "clamp(40px, 3.33vh, 48px)",
  itemPx: "clamp(12px, 1.11vw, 16px)",
  itemGap: "clamp(12px, 1.11vw, 16px)",
  itemRadius: "clamp(8px, 0.83vw, 12px)",
  itemIconSize: "clamp(18px, 1.39vw, 20px)",
  itemFont: "clamp(14px, 1.04vw, 15px)",
  sectionSep: "clamp(16px, 1.67vh, 24px)",
  sectionH: "clamp(44px, 4.86vh, 56px)",
  sectionPx: "clamp(16px, 1.67vw, 24px)",
  sectionIconSize: "clamp(20px, 1.67vw, 24px)",
  sectionFont: "clamp(14px, 1.11vw, 16px)",
  childPadLeft: "clamp(36px, 2.78vw, 40px)",
  footerPad: "clamp(16px, 1.67vh, 24px)",
} as const;

/* ── Types ── */
interface DashboardSidebarProps {
  activeSection: DashboardSectionId;
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (sectionId: DashboardSectionId) => void;
  onSignOut?: () => void;
}

interface NavItemDef {
  id: DashboardSectionId;
  label: string;
  icon: LucideIcon;
  badge?: string;
}

interface ExpandableChild {
  id: string;
  label: string;
  icon: LucideIcon;
}

/* ── Nav config ── */
const NAV_ITEMS: NavItemDef[] = [
  { id: "projects", label: "Projects", icon: LayoutDashboard },
  { id: "assets", label: "Assets", icon: Package },
  { id: "messages", label: "Messages", icon: MessageSquare },
];

/**
 * Client role sidebar — exactly 3 items in specified order (Req 11.8, 11.10).
 * "Projects" renders the ClientWorkspacePage for the client role.
 */
const CLIENT_NAV_ITEMS: NavItemDef[] = [
  { id: "projects", label: "Projects", icon: FolderOpen },
  { id: "messages", label: "Messages", icon: MessageSquare },
  { id: "assets", label: "Assets", icon: Image },
];

const MANUALS_CHILDREN: ExpandableChild[] = [
  { id: "manuals-all", label: "All Manuals", icon: BookOpen },
  { id: "manuals-drafts", label: "Drafts", icon: BookOpen },
  { id: "manuals-published", label: "Published", icon: BookOpen },
];

const FOOTER_ITEMS: NavItemDef[] = [
  { id: "settings", label: "Settings", icon: Settings },
];

/* ── Logo Image ── */
function SidebarLogo({ size }: { size: string }) {
  return (
    <img
      src="/sidebar-logo.svg"
      alt="Logo"
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: "6px",
        objectFit: "cover",
      }}
    />
  );
}

/* ── SidebarNavItem ── */
function SidebarNavItem({
  icon: Icon,
  label,
  isActive,
  isCollapsed,
  badge,
  onClick,
  ariaLabel,
  paddingLeft,
}: {
  icon: LucideIcon;
  label: string;
  isActive: boolean;
  isCollapsed: boolean;
  badge?: string;
  onClick: () => void;
  ariaLabel?: string;
  paddingLeft?: string;
}) {
  const badgeGradient = "linear-gradient(90deg, #FD936E 0%, #8181FF 100%)";

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel ?? label}
      aria-current={isActive ? "page" : undefined}
      className={`sidebar-nav-item${isActive ? " sidebar-nav-item--active" : ""}`}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: isCollapsed ? "center" : "flex-start",
        width: "100%",
        height: clamp.itemH,
        paddingLeft: paddingLeft ?? (isCollapsed ? "0" : clamp.itemPx),
        paddingRight: isCollapsed ? "0" : clamp.itemPx,
        gap: clamp.itemGap,
        borderRadius: clamp.itemRadius,
        border: isActive ? undefined : "none",
        background: isActive ? undefined : "transparent",
        cursor: "pointer",
        transition: "all 300ms ease-in-out",
        fontFamily: "'DM Sans', sans-serif",
        fontSize: clamp.itemFont,
        fontVariationSettings: "'opsz' 14, 'wght' 500",
        lineHeight: "1.5",
        color: isActive ? undefined : "var(--sidebar-text, #202224)",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        textAlign: "left",
      }}
    >
      <Icon
        size={18}
        strokeWidth={2}
        className="sidebar-nav-icon"
        style={{
          width: clamp.itemIconSize,
          height: clamp.itemIconSize,
          flexShrink: 0,
          color: isActive ? undefined : "var(--sidebar-icon, #000000)",
          transition: "color 200ms",
        }}
      />
      {!isCollapsed && (
        <>
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              transition: "color 200ms",
            }}
          >
            {label}
          </span>
          {badge && (
            <span
              style={{
                marginLeft: "auto",
                flexShrink: 0,
                borderRadius: "9999px",
                paddingLeft: clamp.badgePx,
                paddingRight: clamp.badgePx,
                paddingTop: clamp.badgePy,
                paddingBottom: clamp.badgePy,
                background: badgeGradient,
                color: "#FFFFFF",
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 600,
                fontSize: clamp.badgeFont,
                lineHeight: "1.33",
              }}
            >
              {badge}
            </span>
          )}
        </>
      )}
    </button>
  );
}

/* ── Main component ── */
export function DashboardSidebar({
  activeSection,
  isOpen,
  onClose,
  onNavigate,
  onSignOut,
  manualsRemaining = 0,
  hasPurchasedManualPack = false,
  role = 'client',
}: DashboardSidebarProps & {
  manualsRemaining?: number;
  hasPurchasedManualPack?: boolean;
  role?: DashboardRole;
}) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isManualsExpanded, setManualsExpanded] = useState(true);
  const [isManualsModalOpen, setManualsModalOpen] = useState(() => {
    // Auto-open the modal when returning from Stripe checkout (success or canceled)
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      return params.has('checkout') && params.has('upload_id');
    }
    return false;
  });

  // Use role permissions to determine which sidebar items to render (Req 11.8, 11.9, 11.10)
  const permissions = useRolePermissions(role);
  const isClientRole = role === 'client';

  // Select nav items based on role: client sees exactly 3 items, others see full set
  const navItems = useMemo(() => {
    if (isClientRole) return CLIENT_NAV_ITEMS;
    return NAV_ITEMS;
  }, [isClientRole]);

  // Footer items: client role has no Settings (Req 11.8 — hide all other sidebar items)
  const footerItems = useMemo(() => {
    if (isClientRole) return [];
    return FOOTER_ITEMS;
  }, [isClientRole]);

  // Whether to show the manuals card (only for non-client roles with purchase permission)
  const showManualsCard = !isClientRole;

  const sidebarWidth = isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH;

  const toggleCollapse = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  const handleExpandableChildClick = useCallback(
    (childId: string) => {
      if (isCollapsed) {
        setIsCollapsed(false);
        setManualsExpanded(true);
      }
      // For now child items don't map to sections — just expand
    },
    [isCollapsed],
  );

  return (
    <>
      {/* Mobile backdrop */}
      <div
        aria-hidden={!isOpen}
        className={`fixed inset-0 z-40 bg-slate-50/70 dark:bg-slate-950/70 backdrop-blur-sm lg:hidden ${isOpen ? "block" : "hidden"}`}
        onClick={onClose}
        data-oid="aa0a1i0"
      />

      {/* Sidebar */}
      <aside
        aria-label="Main navigation"
        className={`sidebar-aside fixed inset-y-0 left-0 z-50 lg:static lg:z-auto ${
          isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: `${sidebarWidth}px`,
          minWidth: `${sidebarWidth}px`,
          maxWidth: `${sidebarWidth}px`,
          background: "var(--sidebar-bg, #FFFFFF)",
          borderRight: "1px solid var(--sidebar-border, #e0e0e0)",
          transition: "all 300ms ease-in-out",
          overflow: "hidden",
          fontFamily: "'DM Sans', sans-serif",
          fontVariationSettings: "'opsz' 14, 'wght' 500",
        }}
        data-oid="woagn:_"
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            paddingLeft: clamp.headerPx,
            paddingRight: clamp.headerPx,
            gap: clamp.headerPx,
            minHeight: clamp.headerMinH,
            flexShrink: 0,
          }}
        >
          <SidebarLogo size={clamp.logoSize} />
          {!isCollapsed && (
            <span
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "12px",
                paddingLeft: clamp.badgePx,
                paddingRight: clamp.badgePx,
                paddingTop: clamp.badgePy,
                paddingBottom: clamp.badgePy,
                background: "linear-gradient(90deg, #FD936E 0%, #8181FF 100%)",
                boxShadow: "0px 1px 2px 0px rgba(0,0,0,0.3), 0px 1px 3px 1px rgba(0,0,0,0.15)",
                color: "#FFFFFF",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: clamp.badgeFont,
                fontWeight: "normal",
                lineHeight: "normal",
              }}
            >
              Free
            </span>
          )}
        </div>

        {/* Navigation area */}
        <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
          <nav
            aria-label="Main menu"
            style={{
              display: "flex",
              flexDirection: "column",
              paddingLeft: clamp.navPadX,
              paddingRight: clamp.navPadX,
              paddingTop: clamp.navPadTop,
              gap: clamp.navGap,
            }}
          >
            {/* Main nav items */}
            {navItems.map((item) => (
              <SidebarNavItem
                key={item.id}
                icon={item.icon}
                label={item.label}
                isActive={activeSection === item.id}
                isCollapsed={isCollapsed}
                badge={item.badge}
                onClick={() => onNavigate(item.id)}
              />
            ))}

            {/* Expandable "Manuals" section — hidden until feature is ready */}
            {false && <div style={{ marginTop: clamp.sectionSep }}>
              {/* Parent button */}
              <button
                type="button"
                aria-expanded={isManualsExpanded}
                aria-label="Manuals menu"
                onClick={() => {
                  if (isCollapsed) {
                    setIsCollapsed(false);
                    setManualsExpanded(true);
                  } else {
                    setManualsExpanded((prev) => !prev);
                  }
                }}
                className="sidebar-nav-item"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: isCollapsed ? "center" : "flex-start",
                  width: "100%",
                  height: clamp.sectionH,
                  paddingLeft: isCollapsed ? "0" : clamp.sectionPx,
                  paddingRight: isCollapsed ? "0" : clamp.sectionPx,
                  gap: clamp.itemGap,
                  borderRadius: "6px",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  transition: "all 300ms ease-in-out",
                  fontFamily: "'DM Sans', sans-serif",
                  color: "var(--sidebar-text, #000000)",
                }}
              >
                <BookOpen
                  size={20}
                  strokeWidth={2}
                  style={{
                    width: clamp.sectionIconSize,
                    height: clamp.sectionIconSize,
                    flexShrink: 0,
                    color: "var(--sidebar-icon, #000000)",
                  }}
                />
                {!isCollapsed && (
                  <>
                    <span
                      style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: clamp.sectionFont,
                        fontVariationSettings: "'opsz' 14, 'wght' 500",
                        lineHeight: "24px",
                        color: "var(--sidebar-text, #000000)",
                      }}
                    >
                      Manuals
                    </span>
                    <ChevronDown
                      size={20}
                      strokeWidth={2}
                      style={{
                        marginLeft: "auto",
                        width: clamp.sectionIconSize,
                        height: clamp.sectionIconSize,
                        color: "var(--sidebar-icon, #000000)",
                        transition: "transform 300ms",
                        transform: isManualsExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                      }}
                    />
                  </>
                )}
              </button>

              {/* Children container */}
              {!isCollapsed && (
                <div
                  style={{
                    overflow: "hidden",
                    transition: "all 300ms ease-in-out",
                    maxHeight: isManualsExpanded ? `${MANUALS_CHILDREN.length * 56}px` : "0px",
                    opacity: isManualsExpanded ? 1 : 0,
                  }}
                >
                  {MANUALS_CHILDREN.map((child) => (
                    <SidebarNavItem
                      key={child.id}
                      icon={child.icon}
                      label={child.label}
                      isActive={false}
                      isCollapsed={false}
                      onClick={() => handleExpandableChildClick(child.id)}
                      paddingLeft={clamp.childPadLeft}
                    />
                  ))}
                </div>
              )}
            </div>}
          </nav>
        </div>

        {/* Available 3D Manuals card — above footer (hidden for client role per Req 11.8) */}
        {showManualsCard && (() => {
          const manualsRemainingVal = Number.isFinite(Number(manualsRemaining))
            ? manualsRemaining
            : 0;
          const purchased = Boolean(hasPurchasedManualPack);
          const showPurchase = (role === 'admin' || role === 'superadmin') && (!purchased || manualsRemainingVal <= 0);
          const isZero = manualsRemainingVal === 0;

          if (isCollapsed) return null;

          const CardInner = (
            <div className="mb-3" data-oid="ypuywn0">
              <div className="flex items-center justify-between">
                <span
                  style={{
                    fontSize: "0.65rem",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.12em",
                    color: "var(--sidebar-text, #202224)",
                  }}
                >
                  3D Manuals Remaining
                </span>
                {showPurchase ? (
                  <strong style={{ fontSize: "0.875rem", fontWeight: 600, color: "#8181FF" }}>
                    Purchase
                  </strong>
                ) : (
                  <strong
                    className="flex items-baseline gap-2"
                    style={{ fontSize: "1.125rem", fontWeight: 700, color: "var(--sidebar-text, #202224)" }}
                  >
                    <span>{manualsRemainingVal}</span>
                    <span style={{ fontSize: "0.75rem", fontWeight: 500, color: "var(--sidebar-text, #202224)" }}>
                      left
                    </span>
                  </strong>
                )}
              </div>
              <div className="mt-3">
                <div
                  className="overflow-hidden"
                  style={{
                    height: "10px",
                    borderRadius: "9999px",
                    background: "var(--sidebar-bar-bg, #e2e8f0)",
                  }}
                >
                  <span
                    className="block h-full rounded-full"
                    style={{
                      width: `${isZero ? 4 : Math.min(100, (manualsRemainingVal / 10) * 100)}%`,
                      background: isZero
                        ? "rgba(128,128,128,0.4)"
                        : "linear-gradient(90deg, #8181FF 0%, #FD936E 100%)",
                    }}
                  />
                </div>
              </div>
              <p
                className="mt-2 text-left"
                style={{ fontSize: "11px", color: "var(--sidebar-text, #202224)" }}
              >
                {isZero ? "You've used all purchased manuals" : `${manualsRemainingVal} manuals left`}
              </p>
            </div>
          );

          const cardStyle: React.CSSProperties = {
            marginTop: "0.5rem",
            marginLeft: clamp.navPadX,
            marginRight: clamp.navPadX,
            marginBottom: clamp.navPadX,
            borderRadius: "12px",
            padding: "12px",
            border: "1px solid var(--sidebar-border, #e0e0e0)",
            background: "var(--sidebar-card-bg, rgba(0,0,0,0.02))",
          };

          if (showPurchase) {
            if (isZero) {
              return (
                <button
                  type="button"
                  className="animate-pulse"
                  style={{
                    ...cardStyle,
                    borderImage: "linear-gradient(90deg, #8181FF, #FD936E) 1",
                  }}
                  data-oid="available-manuals-card"
                  onClick={() => setManualsModalOpen(true)}
                >
                  {CardInner}
                </button>
              );
            }
            return (
              <button
                type="button"
                style={cardStyle}
                data-oid="available-manuals-card"
                onClick={() => setManualsModalOpen(true)}
              >
                {CardInner}
              </button>
            );
          }

          return (
            <div style={cardStyle} data-oid="available-manuals-card">
              {CardInner}
            </div>
          );
        })()}

        {/* Footer */}
        <div
          style={{
            flexShrink: 0,
            width: "100%",
            borderTop: "1px solid var(--sidebar-border, #e0e0e0)",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              paddingTop: clamp.footerPad,
              paddingBottom: clamp.footerPad,
              paddingLeft: clamp.navPadX,
              paddingRight: clamp.navPadX,
              gap: clamp.navGap,
            }}
          >
            {footerItems.map((item) => (
              <SidebarNavItem
                key={item.id}
                icon={item.icon}
                label={item.label}
                isActive={false}
                isCollapsed={isCollapsed}
                onClick={() => onNavigate(item.id)}
              />
            ))}
            <SidebarNavItem
              icon={LogOut}
              label="Log out"
              isActive={false}
              isCollapsed={isCollapsed}
              onClick={() => {
                onSignOut?.();
              }}
            />
          </div>
        </div>
      </aside>

      {/* Collapse toggle button */}
      <button
        type="button"
        aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        aria-expanded={!isCollapsed}
        onClick={toggleCollapse}
        className="hidden lg:flex items-center justify-center"
        style={{
          position: "fixed",
          top: clamp.headerMinH,
          transform: "translateY(-50%)",
          left: `calc(${sidebarWidth}px - 12px)`,
          width: "24px",
          height: "24px",
          borderRadius: "50%",
          background: "var(--sidebar-bg)",
          border: "1px solid var(--sidebar-border)",
          boxShadow: "var(--sidebar-toggle-shadow, 0 1px 3px rgba(0,0,0,0.12))",
          zIndex: 9999,
          cursor: "pointer",
          transition: "left 300ms ease-in-out",
        }}
      >
        {isCollapsed ? (
          <ChevronRight size={14} strokeWidth={2} color="var(--sidebar-icon)" aria-hidden="true" />
        ) : (
          <ChevronLeft size={14} strokeWidth={2} color="var(--sidebar-icon)" aria-hidden="true" />
        )}
      </button>

      {isManualsModalOpen ? (
        <ManualsPurchaseModal onClose={() => setManualsModalOpen(false)} />
      ) : null}
    </>
  );
}
